
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import "./Login.scss";

import {
  loginAdmin,
  getStaffPermissions,
} from "../../redux/services/authService/authApi";

import { setLogin } from "../../redux/features/authSlice";
import { setToken } from "../../redux/apiAxios/cookies";

import loginHero from "../../assets/image/crm-login-hero.png";
import yaanjiLogo from "../../assets/image/logo-light.png";

import { successToast, errorToast } from "../../Utils/toast";
import { MdOutlineMail, MdLockOutline } from "react-icons/md";

function Login() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    // Prevent normal browser form submission and reload
    e.preventDefault();
    e.stopPropagation();

    if (!email.trim() || !password) {
      errorToast("Email & Password required");
      return;
    }

    if (loading) return;

    try {
      setLoading(true);

      const res = await loginAdmin({
        email: email.trim(),
        password,
      });

      console.log("Login API response:", res);

      // Handle failed login returned with HTTP 200
      if (
        res?.success === false ||
        res?.status === false ||
        res?.data?.success === false
      ) {
        errorToast(
          res?.message ||
          res?.data?.message ||
          "Invalid email or password"
        );
        return;
      }

      const message = res?.message;
      const token = res?.data?.token;
      const admin = res?.data?.admin;

      // A successful login must contain a token and admin
      if (!token || !admin?.id) {
        errorToast(
          message || "Invalid email or password"
        );
        return;
      }

      // Save authentication data
      setToken(token);

      localStorage.setItem("admin", JSON.stringify(admin));
      localStorage.setItem("role", admin?.role ?? "");
      localStorage.setItem("status", admin?.status ?? "");

      // Get staff permissions
      const permissionRes = await getStaffPermissions(admin.id);

      if (!permissionRes?.data) {
        throw new Error("Unable to fetch staff permissions");
      }

      // Update Redux state
      dispatch(
        setLogin({
          admin,
          token,
          role: permissionRes.data.role,
          permissions: permissionRes.data.staff_permission,
          category_id: admin.category_id,
          subcategory_id: admin.subcategory_id,
        })
      );

      successToast(message || "Login successful");
      navigate("/");
    } catch (error) {
      console.error("Login error:", error);
      console.error("API response:", error?.response?.data);
      console.error("HTTP status:", error?.response?.status);

      const statusCode = error?.response?.status;

      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error;

      if (statusCode === 401 || statusCode === 403) {
        errorToast(apiMessage || "Invalid email or password");
      } else if (statusCode === 422) {
        errorToast(apiMessage || "Please check your email and password");
      } else if (!error?.response && error?.request) {
        errorToast("Unable to connect to server. Please try again.");
      } else {
        errorToast(
          apiMessage ||
          error?.message ||
          "Login failed. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="login-page dark-login">
      <div className="login-split">

        {/* LEFT: CRM WORKSPACE */}
        <section className="login-visual">
          <img
            className="login-hero-image"
            src={loginHero}
            alt="CRM workspace"
          />

          <div className="login-visual-overlay">
            {/* <div className="login-left-brand">
              <img src={yaanjiLogo} alt="Yaanji" />
            </div> */}

            <div className="login-visual-copy">
              <span className="login-badge">
                PROFESSIONAL CRM
              </span>

              <h1>
                Lead.
                <br />
                Convert.
                <br />
                <span>Grow.</span>
              </h1>

              <p>
                A dark, high-contrast command center for your team —
                same APIs, sharper visibility.
              </p>

              <div className="login-hero-features">
                <div className="login-feature">
                  <span className="feature-icon">✓</span>
                  <span>Smart Lead Management</span>
                </div>

                <div className="login-feature">
                  <span className="feature-icon">✓</span>
                  <span>Team &amp; Permission Control</span>
                </div>

                <div className="login-feature">
                  <span className="feature-icon">✓</span>
                  <span>Centralized Administration</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* RIGHT: LOGIN FORM */}
        <section className="login-form-col">
          <div className="login-card">

            <div className="login-brand-header">
              <div className="login-brand-logo">
                <img src={yaanjiLogo} alt="Yaanji Logo" />
              </div>

              <div className="login-brand-divider" />

              <div className="login-brand-caption">
                <h2>Admin Portal</h2>
                <span>MANAGEMENT CONSOLE</span>
              </div>
            </div>

            <div className="login-welcome">
              <h3>Welcome back</h3>
              <p>Sign in to access your admin dashboard.</p>
            </div>

            <form
              onSubmit={handleLogin}
              noValidate={false}
            >
              <div className="form-group">
                <label htmlFor="login-email">
                  Email Address
                </label>

                <div className="input-box">
                  <span className="input-icon" aria-hidden="true">
                    <MdOutlineMail />
                  </span>

                  <input
                    id="login-email"
                    type="email"
                    name="email"
                    autoComplete="username"
                    value={email}
                    className="form-control"
                    placeholder="Enter your email"
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="login-password">
                  Password
                </label>

                <div className="input-box">
                  <span className="input-icon" aria-hidden="true">
                    <MdLockOutline />
                  </span>

                  <input
                    id="login-password"
                    type="password"
                    name="password"
                    autoComplete="current-password"
                    value={password}
                    className="form-control"
                    placeholder="Enter your password"
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <button
                type="submit"
                className="login-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="login-spinner" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <span>Login</span>
                    <span
                      className="login-btn-arrow"
                      aria-hidden="true"
                    >
                      →
                    </span>
                  </>
                )}
              </button>
            </form>

            <div className="login-security-note">
              <span className="security-icon" aria-hidden="true">
                <MdLockOutline />
              </span>

              <span>
                Secure access to your administration panel
              </span>
            </div>

            <div className="login-bottom">
              <span className="login-bottom-line" />

              <p>
                Powered by <strong>Yaanji</strong>
              </p>

              <span className="login-bottom-line" />
            </div>

          </div>
        </section>

      </div>
    </main>
  );
}

export default Login;
